/**
 * The order a statement import runs in, kept out of the sheet so a test can hold it to it: every
 * selected row is inserted first, then the server is asked about bank reversals over the whole
 * batch, and only then is a repayment booked — for the credits its answer leaves free. Once the
 * bookings are in, the server is asked once more, for the flags.
 *
 * Booking as each row went in is how a reversed GHS 1,600 became a repayment on a driver's
 * contract: the check cannot clear a credit whose reversal is still a row further down the
 * statement. So nothing here books before `checkReversals` has returned, and when it returns no
 * answer nothing is booked at all (`planBookings`).
 *
 * The database is handed in (`deps`), never imported, so the order is testable without one.
 */

import { flagAfterBooking, isMatchedCredit, parseReversalAnswer, planBookings } from './importReversals';
import type { Booking, BookingPlan, ImportCounts, InsertedRow, ReversalAnswer, SkippedBooking } from './importReversals';

/** What the insert loop counted, before anything is booked */
export type InsertCounts = Omit<ImportCounts, 'booked' | 'bookingFailed'>;

/** A statement row as the sheet holds it: everything an inserted row carries but its id */
export type StatementRow = Omit<InsertedRow, 'id'>;

export interface BookingDeps {
  /** `get_transaction_reversals(p_batch_ids)` — read-only */
  rpc: (ids: number[]) => PromiseLike<{ data: unknown; error: unknown }>;
  insertRepayment: (booking: Booking) => PromiseLike<{ error: unknown }>;
  sleep: (ms: number) => Promise<void>;
}

export interface ImportDeps<T extends StatementRow> extends BookingDeps {
  /** One row into `transactions`: its id, or the database's refusal */
  insertTransaction: (row: T) => PromiseLike<{ id: number | null; error: { code?: string; message: string } | null }>;
}

export interface RetryDeps extends BookingDeps {
  /** `get_transaction_purposes(transaction_ids)` — what each transaction is booked as now */
  purposes: (ids: number[]) => PromiseLike<{
    data: { transaction_id: number; purpose: string }[] | null;
    error: { message: string } | null;
  }>;
}

export interface Settled {
  plan: BookingPlan;
  booked: number;
  bookingFailed: number;
}

/** An import whose bookings wait on a reversal check that did not answer: every row it inserted */
export interface AwaitingBooking {
  rows: InsertedRow[];
  /** The import's own counts, so a retried booking restates ITS headline */
  counts: InsertCounts;
}

/** Several waiting imports as one Retry: all their rows, and their counts added up */
export function mergeAwaiting(batches: readonly AwaitingBooking[]): AwaitingBooking {
  return {
    rows: batches.flatMap(b => b.rows),
    counts: batches.reduce<InsertCounts>((s, { counts: c }) => ({
      imported: s.imported + c.imported,
      rejected: s.rejected + c.rejected,
      failed: s.failed + c.failed,
      failure: s.failure || c.failure,
    }), { imported: 0, rejected: 0, failed: 0, failure: '' }),
  };
}

/**
 * What waits for Retry once a run settles. A later import never drops an earlier one's waiting
 * rows; a Retry resolves exactly the imports it took, and an import that settled while it ran
 * stays. `waiting` is the list as it stands NOW, not as it stood when the run began. `own` is this
 * run's rows when its check did not answer either.
 */
export function stillAwaiting(
  waiting: readonly AwaitingBooking[],
  retried: readonly AwaitingBooking[],
  own: AwaitingBooking | null,
): AwaitingBooking[] {
  const rest = waiting.filter(b => !retried.includes(b));
  return own ? [...rest, own] : rest;
}

const NOTHING_INSERTED: ReversalAnswer = { reversed_in_batch: [], withheld: [], reports: [] };

/*
 * The reversal check only reads: it answers about this batch's ids as the table stands at the
 * call, so asking again after a dropped call costs nothing and changes nothing. Before booking it
 * gets three attempts, 1 s then 2 s apart; an answer of the wrong shape counts as no answer.
 */
export async function checkReversals(ids: number[], deps: BookingDeps, attempts = 3): Promise<ReversalAnswer | null> {
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) await deps.sleep(attempt * 1000);
    try {
      const { data, error } = await deps.rpc(ids);
      if (error) {
        console.error('Bank-reversal check failed:', error);
        continue;
      }
      const answer = parseReversalAnswer(data);
      if (answer) return answer;
      console.error('Bank-reversal check answered in an unexpected shape:', data);
    } catch (err) {
      console.error('Bank-reversal check failed:', err);
    }
  }
  return null;
}

/**
 * Asks about `rows`, books what the answer allows, then asks once more so the summary flags what
 * the bookings themselves may have made of a reversal (`flagAfterBooking`). With no first answer
 * nothing is booked and the plan's `pending` keeps every matched credit, so the sheet can ask
 * again — recovery, not a gate. The second question books nothing and holds nothing back; when it
 * goes unanswered the first answer's flags stand, and the next import says the rest, since a
 * reversed transaction that is still booked is flagged at every import until it is unbooked.
 */
export async function settleBookings(rows: InsertedRow[], deps: BookingDeps): Promise<Settled> {
  const ids = rows.map(r => r.id);
  const answer = rows.length > 0 ? await checkReversals(ids, deps) : NOTHING_INSERTED;
  const plan = planBookings(rows, answer);

  let booked = 0;
  let bookingFailed = 0;
  for (const booking of plan.bookings) {
    const { error } = await deps.insertRepayment(booking);
    if (!error) booked++;
    else {
      bookingFailed++;
      console.error('Failed to link repayment:', error);
    }
  }

  const after = answer && rows.length > 0 ? await checkReversals(ids, deps, 1) : null;
  return { plan: flagAfterBooking(plan, after), booked, bookingFailed };
}

/**
 * Retry booking. The pending credits sat unassigned since the import, and somebody may have
 * assigned one meanwhile — as a repayment, or as something else entirely, and nothing in the
 * database stops one transaction being claimed by two tables. So first ask what each is booked as
 * now, and book only the rest. The
 * 'reversal' purpose is not a booking (it is how the lists name a leg they hide) and is left to the
 * reversal check. A skipped credit stays in the batch, so the check still weighs it, but loses its
 * match, so it is never booked or offered again. A failed question books nothing: the caller's
 * Retry stays.
 */
export async function retryBookings(
  rows: InsertedRow[],
  deps: RetryDeps,
): Promise<Settled & { rows: InsertedRow[]; skipped: SkippedBooking[] }> {
  const ids = rows.filter(isMatchedCredit).map(r => r.id);
  const booked = new Map<number, string[]>();
  if (ids.length > 0) {
    const { data, error } = await deps.purposes(ids);
    if (error) throw error;
    for (const p of data ?? []) {
      if (p.purpose !== 'reversal') booked.set(p.transaction_id, [...(booked.get(p.transaction_id) ?? []), p.purpose]);
    }
  }

  const skipped = rows.filter(r => booked.has(r.id)).map(row => ({ row, purpose: booked.get(row.id)!.join(', ') }));
  const free = rows.map(r => (booked.has(r.id) ? { ...r, matchedContractId: null, matchedDriverName: null } : r));
  return { ...(await settleBookings(free, deps)), rows: free, skipped };
}

/** Inserts every row, and only then settles the bookings for the ones that went in */
export async function importStatement<T extends StatementRow>(
  rows: T[],
  deps: ImportDeps<T>,
): Promise<Settled & { inserted: InsertedRow[]; counts: InsertCounts }> {
  const inserted: InsertedRow[] = [];
  let imported = 0;
  let rejected = 0;
  let failed = 0;
  let failure = '';

  for (const t of rows) {
    const { id, error } = await deps.insertTransaction(t);
    if (error) {
      console.error('Failed to insert transaction:', error);
      // Only a unique-violation is about the reference. Reporting a permission denial, a bad
      // timestamp or a dropped connection as one sends the admin to retype a reference that was
      // never the problem — and an edited reference is the dedup key the NEXT statement is
      // matched against, so that detour quietly costs future duplicate detection.
      if (error.code === '23505') {
        rejected++;
      } else {
        failed++;
        if (!failure) failure = error.message;
      }
      continue;
    }

    imported++;
    if (id != null) {
      inserted.push({
        id,
        type: t.type,
        amount: t.amount,
        fee: t.fee,
        currency: t.currency,
        description: t.description,
        matchedContractId: t.matchedContractId,
        matchedDriverName: t.matchedDriverName,
      });
    }
  }

  const counts: InsertCounts = { imported, rejected, failed, failure };
  return { ...(await settleBookings(inserted, deps)), inserted, counts };
}
